import { useCallback } from "react";
import { Loader2, Mic, Square } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useUploadMutation } from "@/hooks/uploads/useUploadMutation";
import { cn } from "@/lib/utils";
import AgentSelection from "../AgentSelection";
import { UploadComponentProps } from "./types";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface UploadDeepResearchProps extends UploadComponentProps {
  researchQuery: string;
  researchTitle: string;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  isRecording: boolean;
  isTranscribing: boolean;
  onResearchQueryChange: (query: string) => void;
  onResearchTitleChange: (title: string) => void;
  onStartRecording: () => void;
  onStopRecording: () => void;
}

const UploadDeepResearch = ({
  availableAgents,
  selectedAgentId,
  onAgentSelect,
  researchQuery,
  researchTitle,
  textareaRef,
  isRecording,
  isTranscribing,
  onResearchQueryChange,
  onResearchTitleChange,
  onStartRecording,
  onStopRecording,
  onUploadSuccess,
  onDeferredActionAdd,
}: UploadDeepResearchProps) => {
  const deepResearchMutation = useUploadMutation("deep-research", selectedAgentId, onUploadSuccess);

  const handleResearchQueryChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onResearchQueryChange(e.target.value);
    },
    [onResearchQueryChange],
  );

  return (
    <>
      <AgentSelection
        agents={availableAgents}
        selectedAgentId={selectedAgentId}
        onAgentSelect={onAgentSelect}
      />
      <div className="flex flex-col items-center justify-center h-full max-w-2xl mx-auto">
        <GenieIcon name="brain" size={64} color="#4E9CFF" gradientEnabled className="mb-4" />
        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Deep Research</h3>
        <p className="text-sm text-gray-600 mb-6 text-center">
          Comprehensive research with in-depth analysis and multiple sources
        </p>
        <div className="w-full space-y-3">
          <Input
            type="text"
            placeholder="Optional: Custom title for research document"
            value={researchTitle}
            onChange={(e) => onResearchTitleChange(e.target.value)}
            className="flex-1 h-11 sm:h-12 text-sm sm:text-base"
            disabled={deepResearchMutation.isPending}
          />
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Textarea
                ref={textareaRef}
                placeholder="Enter your research query..."
                value={researchQuery}
                onChange={handleResearchQueryChange}
                className="min-h-[44px] sm:min-h-[48px] text-sm sm:text-base pr-12 resize-none overflow-hidden"
                disabled={deepResearchMutation.isPending}
                rows={1}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={isRecording ? onStopRecording : onStartRecording}
                disabled={isTranscribing || deepResearchMutation.isPending}
                className={cn(
                  "absolute right-1 top-2 h-9 w-9 p-0",
                  isRecording && "text-red-600 hover:text-red-700",
                )}
              >
                {isRecording ? (
                  <Square className="h-4 w-4" />
                ) : isTranscribing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Mic className="h-4 w-4" />
                )}
              </Button>
            </div>
            <Button
              onClick={() => {
                if (!researchQuery.trim()) {
                  toast.error("Please enter a research topic");
                  return;
                }

                const title = researchTitle || researchQuery.split(/\s+/).slice(0, 5).join(" ");

                if (onDeferredActionAdd) {
                  onDeferredActionAdd({
                    type: "deep-research",
                    data: {
                      query: researchQuery,
                      title,
                    },
                  });
                  toast.success(`Deep research will be performed after genie creation`);
                  onResearchQueryChange("");
                  onResearchTitleChange("");
                } else {
                  deepResearchMutation.mutate({ query: researchQuery, title });
                  onResearchQueryChange("");
                  onResearchTitleChange("");
                }
              }}
              disabled={!researchQuery || (onDeferredActionAdd ? false : deepResearchMutation.isPending)}
              className="w-full sm:w-auto bg-gradient-to-r from-genie-primary to-genie-secondary text-white font-semibold px-6 sm:px-8 h-11 sm:h-12 shadow-lg hover:shadow-xl transition-all hover:scale-105 rounded-xl"
            >
              {!onDeferredActionAdd && deepResearchMutation.isPending ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Researching...
                </>
              ) : (
                "Deep Research"
              )}
            </Button>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-4 text-center max-w-md">
          🔍 Performs multiple web searches and synthesizes findings for comprehensive analysis
        </p>
      </div>
    </>
  );
};

export default UploadDeepResearch;
